"use client";

import { useState, useMemo } from "react";
import { Button, Card } from "@repo/ui";
import { getNextCohort } from "@repo/constants/between-sessions";
import {
  Users,
  Clock,
  Heart,
  Laptop,
  CheckCircle,
  Star,
  ArrowRight,
  Sparkles,
  GraduationCap,
  MessageCircle,
  Target,
  Zap,
  Shield,
  ChevronDown,
  Briefcase,
  BookOpen,
  Award,
  Headphones,
  MapPin,
  FileText,
  Calendar,
  Lightbulb,
  HandHeart,
  Rocket,
  Percent
} from "lucide-react";

export default function BetweenSessionsStudentPage() {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [activeLab, setActiveLab] = useState(0);

  // Get next cohort intake date
  const nextCohort = useMemo(() => getNextCohort(), []);

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const impactStats = [
    { number: "500+", label: "students supported", icon: GraduationCap, color: "from-purple-100 to-purple-200" },
    { number: "8", label: "months of curriculum", icon: BookOpen, color: "from-blue-100 to-blue-200" },
    { number: "100%", label: "Virtual & flexible", icon: Laptop, color: "from-green-100 to-green-200" }
  ];

  const testimonials = [
    {
      name: "Sarah M.",
      program: "Counselling Psychology Student",
      quote: "Between Sessions completely changed my practicum experience. I was placed at a small clinic where I was the only intern, feeling totally isolated. Having a community of other students going through the same thing made all the difference. The Slack group alone is worth it.",
      image: "SM",
      highlight: "No longer isolated",
      gradient: "from-purple-400 to-pink-400"
    },
    {
      name: "Jordan T.",
      program: "Clinical Psychology Student",
      quote: "The LevelUp Labs curriculum gave me structure that my placement didn't provide. Each month I learned something practical I could immediately use with clients. By month 4, I felt like a completely different therapist than when I started.",
      image: "JT",
      highlight: "Practical skills monthly",
      gradient: "from-blue-400 to-cyan-400"
    },
    {
      name: "Priya K.",
      program: "Counselling Psychology Student",
      quote: "I joined Community tier for the group supervision and it was exactly what I needed. My placement supervisor is great but stretched thin. Having that extra monthly supervision with other students was invaluable for processing difficult cases.",
      image: "PK",
      highlight: "Extra supervision support",
      gradient: "from-green-400 to-emerald-400"
    },
    {
      name: "Alex R.",
      program: "Clinical Psychology Student",
      quote: "The peer group became my lifeline during practicum. We shared resources, vented about tough sessions, and celebrated wins together. I made friendships that I know will last my entire career. Worth every penny of the 8 months.",
      image: "AR",
      highlight: "Lifelong connections",
      gradient: "from-orange-400 to-amber-400"
    }
  ];


  const faqs = [
    {
      question: "What is Between Sessions for students?",
      answer: "Between Sessions is a support community designed for practicum students who want peer connection, professional development, and optional supervision—regardless of where they're doing their placement. You get community, resources, and structured support throughout your practicum.",
      icon: Sparkles
    },
    {
      question: "Do I need to be doing my practicum at Alli to join?",
      answer: "No! Between Sessions is open to all practicum students, regardless of where you're placed. Whether you're at Alli, a hospital, private practice, or community agency, you can join for the community, resources, and optional supervision.",
      icon: MapPin
    },
    {
      question: "What's the difference between Core and Community?",
      answer: "Both are 8-month programs aligned with your practicum. Core ($45/month, $360 total) gives you access to our Slack community, resource library, hours tracking tools, monthly knowledge shares, and LevelUp Labs curriculum. Community ($95/month, $760 total) includes everything in Core plus monthly group supervision, peer groups, and access to à la carte individual supervision.",
      icon: MessageCircle
    },
    {
      question: "What are the LevelUp Labs?",
      answer: "LevelUp Labs is our 8-month curriculum designed to take you from nervous beginner to confident, career-ready therapist. Each month focuses on a different aspect of clinical practice—from managing first-session anxiety to building your professional identity. All tiers have access.",
      icon: Rocket
    },
    {
      question: "How much supervision will I receive in Community tier?",
      answer: "Community tier includes monthly group supervision (90 minutes, up to 8 therapists) led by an experienced supervisor, plus monthly peer group meetings. You also get access to book additional individual supervision at member rates when you need extra support.",
      icon: Users
    },
    {
      question: "What's the time commitment?",
      answer: "Between Sessions is designed to fit around your practicum schedule. Core members can engage as much or as little as they want with the community and resources. Community members commit to attending monthly group supervision and peer group meetings (about 2-3 hours per month of scheduled time).",
      icon: Clock
    },
    {
      question: "When should I join?",
      answer: "Join when your practicum starts! We have cohort intakes three times per year: Winter (January), Spring (May), and Fall (September)—aligned with typical practicum start dates. Each cohort runs for 8 months, so you get support from day one of your practicum until graduation.",
      icon: Calendar
    },
    {
      question: "What happens after I graduate?",
      answer: "After completing your practicum, you can continue with Between Sessions at our regular member rates (no longer student pricing) to maintain the community and supervision support as you transition to independent practice.",
      icon: Award
    }
  ];

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center px-4 overflow-hidden">
        {/* Light gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-white to-violet-50">
          <div className="absolute inset-0 bg-gradient-to-b from-purple-100/30 to-transparent" />
        </div>

        {/* Subtle floating elements */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-100/20 rounded-full filter blur-3xl" />
          <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-violet-100/20 rounded-full filter blur-3xl" />
          <div className="absolute bottom-1/2 left-1/2 w-96 h-96 bg-purple-100/20 rounded-full filter blur-3xl" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-purple-50/80 backdrop-blur-md px-6 py-3 rounded-full mb-8">
            <GraduationCap className="w-5 h-5 text-purple-400" />
            <span className="text-purple-700 font-medium">For Practicum Students</span>
            <GraduationCap className="w-5 h-5 text-purple-400" />
          </div>

          {/* Main headline */}
          <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6">
            Between Sessions
            <span className="block bg-gradient-to-r from-purple-600 to-violet-500 bg-clip-text text-transparent">
              For Students
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto mb-10 leading-relaxed">
            Community, resources, and support for practicum students—wherever you're placed.
          </p>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button
              size="lg"
              className="bg-purple-600 text-white hover:bg-purple-700 px-10 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
              onClick={() => window.location.href = '/between-sessions/join?student=true'}
            >
              Join Between Sessions
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {impactStats.map((stat, index) => (
              <div key={index} className="bg-white/80 backdrop-blur-md rounded-2xl p-6 transform hover:scale-105 transition-all shadow-lg hover:shadow-xl">
                <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mx-auto mb-3`}>
                  <stat.icon className="w-6 h-6 text-gray-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.number}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-purple-400" />
        </div>
      </section>

      {/* Trusted By Schools Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
              Trusted by Students Across Training Programs
            </h3>
            <p className="text-gray-500 text-sm">
              We work with practicum students from leading counselling and psychology programs
            </p>
          </div>

          {/* School Logos Marquee */}
          <div className="relative overflow-hidden">
            <div className="flex animate-marquee">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((num, idx) => (
                <div
                  key={idx}
                  className="flex-shrink-0 mx-8 md:mx-12 grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-300"
                >
                  <img
                    src={`/school_${num}.png`}
                    alt={`Partner school ${num}`}
                    className="h-20 md:h-28 w-auto object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The Problem - Reality Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-rose-50 px-4 py-2 rounded-full mb-6">
              <div className="w-2 h-2 bg-rose-400 rounded-full"></div>
              <span className="text-rose-700 font-medium text-sm">The Reality</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900">
              Most practicum placements<br />
              <span className="text-gray-400">happen in small clinics.</span>
            </h2>
          </div>

          {/* Reality Cards */}
          <div className="grid md:grid-cols-2 gap-5 mb-16">
            {[
              {
                icon: Users,
                title: "You're often the only intern.",
                description: "There's no cohort, no peers going through it with you—just you and your caseload.",
                color: "from-blue-500 to-indigo-600",
                bgColor: "bg-blue-50",
                borderColor: "border-blue-100"
              },
              {
                icon: Clock,
                title: "Your supervisor is busy.",
                description: "They're running a practice, seeing their own clients, and squeezing in your hour when they can.",
                color: "from-amber-500 to-orange-600",
                bgColor: "bg-amber-50",
                borderColor: "border-amber-100"
              },
              {
                icon: Zap,
                title: "You're thrown into sessions.",
                description: "Real clients, real problems—but without the real-world, hands-on training to back it up.",
                color: "from-purple-500 to-violet-600",
                bgColor: "bg-purple-50",
                borderColor: "border-purple-100"
              },
              {
                icon: MessageCircle,
                title: "Nobody talks about the in-between.",
                description: "The self-doubt after a hard session. The questions you don't know who to ask.",
                color: "from-rose-500 to-pink-600",
                bgColor: "bg-rose-50",
                borderColor: "border-rose-100"
              }
            ].map((item, index) => (
              <div
                key={index}
                className={`${item.bgColor} ${item.borderColor} border rounded-2xl p-6 md:p-8 hover:shadow-lg transition-all duration-300 group`}
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          {/* Bottom Statement - The Solution */}
          <div className="bg-gradient-to-br from-purple-100 to-primary-100 rounded-3xl p-8 md:p-12 border border-purple-200">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-xl md:text-2xl text-gray-600 mb-2">
                Placement gives you hours.
              </p>
              <p className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                But hours alone don't make you a confident therapist.
              </p>

              <div className="w-16 h-1 bg-purple-300 mx-auto mb-6 rounded-full"></div>

              <p className="text-lg md:text-xl text-gray-700 mb-6 leading-relaxed">
                That's why we built <span className="font-semibold text-purple-700">Between Sessions</span>—a support system designed specifically for practicum students navigating their placement.
              </p>

              <div className="bg-white/60 rounded-2xl p-6 md:p-8 backdrop-blur-sm">
                <p className="text-gray-800 text-base md:text-lg leading-relaxed">
                  Join our community for peer support, group supervision, resources, and the guidance you need to thrive—wherever you're placed.
                </p>
              </div>

              <p className="text-purple-700 font-medium mt-6 text-base md:text-lg">
                Get the mentorship, community, and guidance that most placements leave out.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included - Feature Comparison with Pricing */}
      <section className="py-24 px-4 bg-gradient-to-b from-slate-50 via-white to-violet-50/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-violet-100/60 px-4 py-2 rounded-full mb-6">
              <Star className="w-4 h-4 text-violet-500" />
              <span className="text-violet-700 font-medium text-sm">8-Month Program · Start with Your Practicum</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Support for Your Entire Practicum
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join when your practicum starts. Get 8 months of community, resources, and growth—wherever you're placed.
            </p>
          </div>

          {/* Pricing Cards with Features */}
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 mb-20 max-w-4xl mx-auto">
            {/* Core Card */}
            <div className="group bg-violet-50/30 rounded-3xl p-6 md:p-8 border border-violet-200/50 hover:border-violet-300/60 hover:shadow-lg transition-all duration-300 flex flex-col">
              <div className="mb-6">
                <div className="inline-flex items-center gap-1.5 bg-violet-100/60 text-violet-700/80 text-xs font-semibold px-2.5 py-1 rounded-full mb-3">
                  <GraduationCap className="w-3 h-3" />
                  Student Membership
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Core</h3>
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="text-lg text-gray-400 line-through">$75</span>
                  <span className="text-4xl font-bold text-gray-900">$45</span>
                  <span className="text-gray-500">/month</span>
                  <span className="text-violet-600/70 text-sm font-medium">Student rate</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">8-month program · $360 total</p>
              </div>

              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                Community, resources, and tools to support your entire practicum journey.
              </p>

              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">What's included</p>
              <ul className="space-y-3 mb-6 flex-grow">
                {[
                  { icon: MessageCircle, text: "Private Slack community", id: "slack-community" },
                  { icon: BookOpen, text: "Full resource library", id: "resource-library" },
                  { icon: Clock, text: "Hours tracking tools", id: "hours-tracking" },
                  { icon: Lightbulb, text: "Monthly knowledge share", id: "knowledge-share" },
                  { icon: Rocket, text: "Monthly LevelUp Labs", id: "levelup-labs" },
                  { icon: Percent, text: "Member discounts", id: "member-discounts" },
                ].map((item, idx) => (
                  <li key={idx}>
                    <a
                      href={`#${item.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="flex items-center gap-2.5 group/item cursor-pointer"
                    >
                      <div className="w-7 h-7 rounded-lg bg-violet-100/60 flex items-center justify-center flex-shrink-0 group-hover/item:bg-violet-200/60 transition-colors">
                        <item.icon className="w-3.5 h-3.5 text-violet-600/80" />
                      </div>
                      <span className="text-gray-700 text-sm group-hover/item:text-violet-600 transition-colors">{item.text}</span>
                    </a>
                  </li>
                ))}
              </ul>

              <div className="bg-violet-50/60 rounded-xl p-4 mb-6">
                <p className="text-xs font-semibold text-violet-600/70 uppercase tracking-wide mb-1">Best if</p>
                <p className="text-sm text-violet-700/80">
                  You want community, shared tools, and a place to grow alongside other students.
                </p>
              </div>

              <Button
                className="w-full bg-violet-500/80 hover:bg-violet-600/80 text-white py-5 text-base font-semibold"
                onClick={() => window.location.href = '/between-sessions/join?tier=core&student=true'}
              >
                Sign Up for Core
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>

            {/* Community Card */}
            <div className="group relative bg-gradient-to-b from-rose-50 to-white rounded-3xl p-6 md:p-8 border-2 border-rose-200/60 hover:border-rose-300 hover:shadow-xl transition-all duration-300 flex flex-col">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-gradient-to-r from-rose-400 to-pink-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg uppercase tracking-wide">
                  Most Popular
                </span>
              </div>

              <div className="mb-6 pt-2">
                <div className="inline-flex items-center gap-1.5 bg-rose-100 text-rose-700 text-xs font-semibold px-2.5 py-1 rounded-full mb-3">
                  <GraduationCap className="w-3 h-3" />
                  Student Membership
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Community</h3>
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="text-lg text-gray-400 line-through">$125</span>
                  <span className="text-4xl font-bold text-gray-900">$95</span>
                  <span className="text-gray-500">/month</span>
                  <span className="text-rose-600 text-sm font-medium">Student/alumni rate</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">8-month program · $760 total</p>
              </div>

              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                Everything in Core plus structured supervision and peer support for your entire practicum.
              </p>

              <div className="bg-violet-100/50 border border-violet-200 rounded-xl p-3 mb-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-violet-600 flex-shrink-0" />
                  <span className="text-violet-800 font-medium text-sm">Everything in Core</span>
                </div>
              </div>

              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Plus</p>
              <ul className="space-y-3 mb-6 flex-grow">
                {[
                  { icon: Users, text: "Monthly group supervision (90 min, up to 8)", id: "group-supervision" },
                  { icon: Heart, text: "Monthly peer group", id: "peer-group" },
                  { icon: Headphones, text: "À la carte supervision access", id: "alacarte-supervision" },
                ].map((item, idx) => (
                  <li key={idx}>
                    <a
                      href={`#${item.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="flex items-center gap-2.5 group/item cursor-pointer"
                    >
                      <div className="w-7 h-7 rounded-lg bg-rose-100 flex items-center justify-center flex-shrink-0 group-hover/item:bg-rose-200 transition-colors">
                        <item.icon className="w-3.5 h-3.5 text-rose-600" />
                      </div>
                      <span className="text-gray-700 text-sm group-hover/item:text-rose-600 transition-colors">{item.text}</span>
                    </a>
                  </li>
                ))}
              </ul>

              <div className="bg-rose-50 rounded-xl p-4 mb-6">
                <p className="text-xs font-semibold text-rose-700 uppercase tracking-wide mb-1">Best if</p>
                <p className="text-sm text-rose-800">
                  You want structured support with group supervision and a peer cohort.
                </p>
              </div>

              <Button
                className="w-full bg-rose-500 hover:bg-rose-600 text-white py-5 text-base font-semibold"
                onClick={() => window.location.href = '/between-sessions/join?tier=community&student=true'}
              >
                Sign Up for Community
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Feature Deep Dives */}
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Feature Details</h3>
              <p className="text-gray-600">Click any feature above to learn more</p>
            </div>

            <div className="space-y-3">
              {/* Core Features */}
              <div id="slack-community" className="scroll-mt-24 bg-white rounded-xl p-5 border border-gray-100 hover:border-violet-200 hover:shadow-md transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">Private Slack Community</h4>
                      <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-[10px] font-bold rounded-full">ALL TIERS</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                      Connect daily with fellow practicum students. Ask questions, share wins, process tough sessions, and build lasting friendships.
                    </p>
                  </div>
                </div>
              </div>

              <div id="resource-library" className="scroll-mt-24 bg-white rounded-xl p-5 border border-gray-100 hover:border-sky-200 hover:shadow-md transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-blue-400 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">Full Resource Library</h4>
                      <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-[10px] font-bold rounded-full">ALL TIERS</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                      Clinical tools, templates, treatment planning guides, intervention cheat sheets, and client-ready resources to share in sessions.
                    </p>
                  </div>
                </div>
              </div>

              <div id="hours-tracking" className="scroll-mt-24 bg-white rounded-xl p-5 border border-gray-100 hover:border-emerald-200 hover:shadow-md transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-400 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">Hours Tracking Tools</h4>
                      <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-[10px] font-bold rounded-full">ALL TIERS</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                      Track DCC, indirect client hours, supervision, and professional development hours in one place. Export easily to stay compliant with your program.
                    </p>
                  </div>
                </div>
              </div>

              <div id="knowledge-share" className="scroll-mt-24 bg-white rounded-xl p-5 border border-gray-100 hover:border-amber-200 hover:shadow-md transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-400 flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">Monthly Knowledge Share Sessions</h4>
                      <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-[10px] font-bold rounded-full">ALL TIERS</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                      Live sessions with expert clinicians on specialized topics. Interactive Q&A, recordings available, counts toward PD hours.
                    </p>
                  </div>
                </div>
              </div>

              <div id="member-discounts" className="scroll-mt-24 bg-white rounded-xl p-5 border border-gray-100 hover:border-pink-200 hover:shadow-md transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-400 to-rose-400 flex items-center justify-center flex-shrink-0">
                    <Percent className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">Member Discounts</h4>
                      <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-[10px] font-bold rounded-full">ALL TIERS</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                      Exclusive discounts on professional liability insurance, continuing education courses, and so much more.
                    </p>
                  </div>
                </div>
              </div>

              {/* LevelUp Labs CTA */}
              <div
                id="levelup-labs-link"
                className="scroll-mt-24 bg-gradient-to-r from-violet-50 to-fuchsia-50 rounded-xl p-5 border-2 border-violet-200/60 hover:border-violet-300 hover:shadow-lg transition-all cursor-pointer group"
                onClick={() => {
                  document.getElementById('levelup-labs')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-400 to-fuchsia-400 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Rocket className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">Monthly LevelUp Labs</h4>
                      <span className="px-2 py-0.5 bg-fuchsia-100 text-fuchsia-700 text-[10px] font-bold rounded-full">8-MONTH CURRICULUM</span>
                      <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-[10px] font-bold rounded-full">ALL TIERS</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                      Our signature curriculum takes you from nervous beginner to confident, career-ready therapist.
                    </p>
                    <div className="flex items-center gap-2 text-violet-600 font-medium text-sm mt-2 group-hover:gap-3 transition-all">
                      <span>See full curriculum below</span>
                      <ChevronDown className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Community Features */}
              <div id="group-supervision" className="scroll-mt-24 bg-white rounded-xl p-5 border border-gray-100 hover:border-rose-200 hover:shadow-md transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-400 to-pink-400 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">Monthly Group Supervision</h4>
                      <span className="px-2 py-0.5 bg-rose-100 text-rose-700 text-[10px] font-bold rounded-full">COMMUNITY</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                      90-minute sessions with up to 8 therapists, led by experienced supervisors. Bring cases, get feedback, learn from peers.
                    </p>
                  </div>
                </div>
              </div>

              <div id="peer-group" className="scroll-mt-24 bg-white rounded-xl p-5 border border-gray-100 hover:border-teal-200 hover:shadow-md transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-cyan-400 flex items-center justify-center flex-shrink-0">
                    <Heart className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">Monthly Peer Group</h4>
                      <span className="px-2 py-0.5 bg-rose-100 text-rose-700 text-[10px] font-bold rounded-full">COMMUNITY</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                      Connect with a small group of peers each month to share experiences, grow together, and learn from each other's clinical journeys.
                    </p>
                  </div>
                </div>
              </div>

              <div id="alacarte-supervision" className="scroll-mt-24 bg-white rounded-xl p-5 border border-gray-100 hover:border-indigo-200 hover:shadow-md transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-400 to-violet-400 flex items-center justify-center flex-shrink-0">
                    <Headphones className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">À la Carte Supervision Access</h4>
                      <span className="px-2 py-0.5 bg-rose-100 text-rose-700 text-[10px] font-bold rounded-full">COMMUNITY</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                      Book individual supervision at member rates when you need extra support beyond your regular sessions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Monthly LevelUp Labs Section */}
      <section id="levelup-labs" className="py-24 px-4 bg-gradient-to-b from-purple-50 via-white to-white overflow-hidden scroll-mt-16">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-100 to-pink-100 px-5 py-2.5 rounded-full mb-6 shadow-sm">
              <Zap className="w-4 h-4 text-purple-600" />
              <span className="text-purple-700 font-semibold text-sm">Included in All Programs</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Monthly LevelUp Labs
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Each month builds on the last, taking you from nervous beginner to confident, career-ready therapist
            </p>
          </div>

          {/* Interactive Lab Viewer */}
          {(() => {
            const labs = [
              {
                month: 1,
                phase: "Foundation",
                title: "Foundations, Client Nerves & Informed Consent",
                description: "Start strong by learning the core skills that help you feel more confident in your first sessions. We will focus on calming those early therapist nerves, building solid habits, and making sure you know how to work safely and clearly with clients from day one.",
                highlights: ["Managing first-session anxiety", "Building solid clinical habits", "Informed consent best practices", "Setting up for success"],
                icon: Shield,
                gradient: "from-blue-500 to-indigo-600",
                lightBg: "bg-blue-50",
                accentColor: "blue"
              },
              {
                month: 2,
                phase: "Foundation",
                title: "Record Keeping & Session Flow",
                description: "Learn how to write notes that actually make sense and support your clinical work. You will also build skills to move smoothly from an intake to follow up sessions so your work feels more organized and less stressful.",
                highlights: ["Clinical documentation that works", "Intake to follow-up transitions", "Staying organized", "Time management in sessions"],
                icon: FileText,
                gradient: "from-violet-500 to-purple-600",
                lightBg: "bg-violet-50",
                accentColor: "violet"
              },
              {
                month: 3,
                phase: "Foundation",
                title: "Client Engagement Skills",
                description: "Build the skills that help clients feel safe, understood, and excited to keep coming back. You will learn how to create strong connections, handle awkward moments, and keep therapy feeling meaningful and helpful.",
                highlights: ["Creating strong connections", "Handling awkward moments", "Keeping clients engaged", "Building therapeutic rapport"],
                icon: Heart,
                gradient: "from-pink-500 to-rose-600",
                lightBg: "bg-pink-50",
                accentColor: "pink"
              },
              {
                month: 4,
                phase: "Foundation",
                title: "Navigating Client Requests & Safety",
                description: "Feel more confident handling tricky questions, big asks, and tough moments in session. You will learn how to trust your clinical thinking and make thoughtful choices instead of feeling stuck or unsure what to say.",
                highlights: ["Handling difficult requests", "Trusting your clinical judgment", "Safety planning", "Setting therapeutic boundaries"],
                icon: Target,
                gradient: "from-amber-500 to-orange-600",
                lightBg: "bg-amber-50",
                accentColor: "amber"
              },
              {
                month: 5,
                phase: "Growth",
                title: "Next Level Skills, Your Niche & Being You",
                description: "Start figuring out what kind of therapist you want to be and who you work best with. You will learn how to talk about your work in a real and honest way and how to grow your caseload without feeling salesy or fake.",
                highlights: ["Finding your therapeutic niche", "Authentic self-presentation", "Growing your caseload", "Marketing without feeling fake"],
                icon: Sparkles,
                gradient: "from-teal-500 to-emerald-600",
                lightBg: "bg-teal-50",
                accentColor: "teal"
              },
              {
                month: 6,
                phase: "Growth",
                title: "Cultivating Affirmative Practice",
                description: "Grow your confidence working with more complex stories and identities. You will learn how to hold your values in the room, support clients in affirming ways, and still take care of your own energy.",
                highlights: ["Working with diverse identities", "Values-aligned practice", "Affirmative approaches", "Therapist self-care"],
                icon: HandHeart,
                gradient: "from-purple-500 to-violet-600",
                lightBg: "bg-purple-50",
                accentColor: "purple"
              },
              {
                month: 7,
                phase: "Growth",
                title: "Professional Identity & Your Path Forward",
                description: "Get clearer on your style, your strengths, and where you want to focus next. You will learn when to keep going with a client, when to ask for help, and when a referral is the best care.",
                highlights: ["Defining your therapeutic style", "Knowing when to refer", "Seeking consultation", "Long-term career vision"],
                icon: Briefcase,
                gradient: "from-indigo-500 to-blue-600",
                lightBg: "bg-indigo-50",
                accentColor: "indigo"
              },
              {
                month: 8,
                phase: "Career Ready",
                title: "Career Ready & What's Next!",
                description: "Look back at how far you have come and get ready for your next step as a therapist. You will leave feeling more confident, more grounded, and way more ready for real world practice.",
                highlights: ["Celebrating your growth", "Next steps planning", "Career readiness", "Joining the alumni network"],
                icon: Award,
                gradient: "from-orange-500 to-red-500",
                lightBg: "bg-orange-50",
                accentColor: "orange"
              }
            ];

            const currentLab = labs[activeLab];
            if (!currentLab) return null;
            const IconComponent = currentLab.icon;

            return (
              <div className="relative">
                {/* Month Selector - Pill Navigation */}
                <div className="flex justify-center mb-8">
                  <div className="inline-flex items-center bg-white rounded-full p-1.5 shadow-lg border border-gray-100">
                    {labs.map((lab, index) => (
                      <button
                        key={lab.month}
                        onClick={() => setActiveLab(index)}
                        className={`relative w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                          activeLab === index
                            ? `bg-gradient-to-br ${lab.gradient} text-white shadow-lg scale-110`
                            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {lab.month}
                        {activeLab === index && (
                          <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] font-medium text-gray-500 whitespace-nowrap">
                            Month {lab.month}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Phase Indicator */}
                <div className="text-center mb-6 mt-8">
                  <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium ${
                    currentLab.phase === 'Foundation'
                      ? 'bg-purple-100 text-purple-700'
                      : currentLab.phase === 'Growth'
                      ? 'bg-pink-100 text-pink-700'
                      : 'bg-orange-100 text-orange-700'
                  }`}>
                    {currentLab.phase === 'Foundation' && 'Phase 1: Building Your Foundation'}
                    {currentLab.phase === 'Growth' && 'Phase 2: Growth & Mastery'}
                    {currentLab.phase === 'Career Ready' && 'Phase 3: Career Readiness'}
                  </span>
                </div>

                {/* Main Content Card */}
                <div className={`relative ${currentLab.lightBg} rounded-3xl p-8 md:p-12 shadow-xl border border-white/50 transition-all duration-500`}>
                  {/* Decorative background gradient */}
                  <div className={`absolute top-0 right-0 w-64 h-64 bg-gradient-to-br ${currentLab.gradient} opacity-5 rounded-full blur-3xl -mr-32 -mt-32`} />

                  <div className="relative z-10">
                    {/* Header with Icon */}
                    <div className="flex flex-col md:flex-row md:items-start gap-6 mb-8">
                      <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${currentLab.gradient} flex items-center justify-center shadow-xl flex-shrink-0`}>
                        <IconComponent className="w-10 h-10 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                          {currentLab.title}
                        </h3>
                        <p className="text-gray-600 text-lg leading-relaxed">
                          {currentLab.description}
                        </p>
                      </div>
                    </div>

                    {/* What You'll Learn */}
                    <div className="bg-white/60 rounded-2xl p-6 backdrop-blur-sm">
                      <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                        What You'll Learn
                      </h4>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {currentLab.highlights.map((highlight, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${currentLab.gradient} flex items-center justify-center flex-shrink-0`}>
                              <CheckCircle className="w-4 h-4 text-white" />
                            </div>
                            <span className="text-gray-700 font-medium">{highlight}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Navigation Arrows */}
                <div className="flex justify-between items-center mt-8">
                  <button
                    onClick={() => setActiveLab(prev => prev > 0 ? prev - 1 : labs.length - 1)}
                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-purple-600 transition-colors group"
                  >
                    <ArrowRight className="w-5 h-5 rotate-180 group-hover:-translate-x-1 transition-transform" />
                    <span className="font-medium">Previous</span>
                  </button>

                  {/* Progress dots for mobile */}
                  <div className="flex gap-1.5 md:hidden">
                    {labs.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveLab(index)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          activeLab === index
                            ? 'w-6 bg-purple-600'
                            : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                      />
                    ))}
                  </div>

                  <button
                    onClick={() => setActiveLab(prev => prev < labs.length - 1 ? prev + 1 : 0)}
                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-purple-600 transition-colors group"
                  >
                    <span className="font-medium">Next</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>

                {/* Progress indicator */}
                <div className="mt-6">
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-gradient-to-r ${currentLab.gradient} transition-all duration-500 rounded-full`}
                      style={{ width: `${((activeLab + 1) / labs.length) * 100}%` }}
                    />
                  </div>
                  <p className="text-center text-sm text-gray-500 mt-3">
                    {activeLab + 1} of {labs.length} months
                  </p>
                </div>
              </div>
            );
          })()}

          {/* Bottom Info */}
          <div className="mt-16 text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-gradient-to-r from-purple-100 via-pink-50 to-purple-100 rounded-2xl p-6 sm:p-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <p className="text-sm text-gray-500">Available in Core & Community</p>
                  <p className="font-bold text-gray-900">Graduate confident & career-ready</p>
                </div>
              </div>
              <Button
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-3 font-semibold shadow-lg hover:shadow-xl transition-all"
                onClick={() => window.location.href = '/between-sessions/join?student=true'}
              >
                Get Started
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Cohort Calendar Section */}
      <section className="py-16 md:py-20 px-4 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <span className="text-purple-600 font-semibold text-sm uppercase tracking-wider">Start With Your Practicum</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-4">
              Aligned With Practicum Start Dates
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Join the cohort that matches when your practicum begins. Each program runs for 8 months—from your first day to graduation.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {/* Winter */}
            <div className={`relative rounded-2xl p-6 border transition-all ${nextCohort.season === 'winter' ? 'bg-sky-50 border-sky-200 shadow-lg ring-2 ring-sky-300' : 'bg-gray-50 border-gray-100 hover:border-gray-200'}`}>
              {nextCohort.season === 'winter' && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 flex gap-2">
                  <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md animate-pulse">
                    Now Accepting
                  </span>
                </div>
              )}
              <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center mb-4 mt-2">
                <span className="text-xl">❄️</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Winter Cohort</h3>
              <p className="text-sky-600 font-medium text-sm mb-2">Starts January 1</p>
              <p className="text-gray-500 text-sm">Runs January through August.</p>
            </div>

            {/* Spring */}
            <div className={`relative rounded-2xl p-6 border transition-all ${nextCohort.season === 'spring' ? 'bg-rose-50 border-rose-200 shadow-lg ring-2 ring-rose-300' : 'bg-gray-50 border-gray-100 hover:border-gray-200'}`}>
              {nextCohort.season === 'spring' && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 flex gap-2">
                  <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md animate-pulse">
                    Now Accepting
                  </span>
                </div>
              )}
              <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center mb-4 mt-2">
                <span className="text-xl">🌸</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Spring Cohort</h3>
              <p className="text-rose-500 font-medium text-sm mb-2">Starts May 1</p>
              <p className="text-gray-500 text-sm">Runs May through December.</p>
            </div>

            {/* Fall */}
            <div className={`relative rounded-2xl p-6 border transition-all ${nextCohort.season === 'fall' ? 'bg-amber-50 border-amber-200 shadow-lg ring-2 ring-amber-300' : 'bg-gray-50 border-gray-100 hover:border-gray-200'}`}>
              {nextCohort.season === 'fall' && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 flex gap-2">
                  <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md animate-pulse">
                    Now Accepting
                  </span>
                </div>
              )}
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4 mt-2">
                <span className="text-xl">🍂</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Fall Cohort</h3>
              <p className="text-amber-600 font-medium text-sm mb-2">Starts September 1</p>
              <p className="text-gray-500 text-sm">Runs September through April.</p>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <p className="text-gray-600 mb-4">
              Starting your practicum in {nextCohort.displayDate}? Join the <span className="font-semibold text-gray-900">{nextCohort.name} Cohort</span>
            </p>
            <Button
              size="lg"
              className="bg-gradient-to-r from-purple-600 to-primary-600 text-white hover:shadow-xl"
              onClick={() => window.location.href = '/between-sessions/join?student=true'}
            >
              Join When Your Practicum Starts <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-24 px-4 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-purple-600 font-semibold text-sm uppercase tracking-wider">Success Stories</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-6">
              Hear From Our Students
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real stories from students who thrived with Between Sessions support
            </p>
          </div>

          <div className="grid lg:grid-cols-4 gap-6 mb-12">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="group">
                <Card className="p-6 h-full hover:shadow-2xl transition-all duration-300 relative overflow-hidden">
                  {/* Gradient accent */}
                  <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${testimonial.gradient}`} />

                  {/* Stars */}
                  <div className="flex gap-1 mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>

                  {/* Quote */}
                  <blockquote className="text-gray-700 mb-6 italic leading-relaxed">
                    "{testimonial.quote}"
                  </blockquote>

                  {/* Author */}
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 bg-gradient-to-br ${testimonial.gradient} rounded-full flex items-center justify-center text-white font-bold text-lg`}>
                      {testimonial.image}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-600">{testimonial.program}</p>
                      <p className="text-xs font-semibold text-purple-600 mt-1">{testimonial.highlight}</p>
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-purple-600 font-semibold text-sm uppercase tracking-wider">FAQ</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-6">
              Your Questions Answered
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-2xl hover:border-purple-300 transition-colors overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-purple-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <faq.icon className="w-6 h-6 text-purple-600" />
                    <h3 className="text-lg font-semibold text-gray-900">{faq.question}</h3>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-purple-600 transition-transform ${expandedFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {expandedFaq === index && (
                  <div className="px-8 pb-6">
                    <p className="text-gray-600 leading-relaxed pl-10">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 md:py-32 px-4 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-violet-600 to-indigo-700" />

        {/* Pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        {/* Floating gradient orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-20 w-80 h-80 bg-pink-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-indigo-400/20 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-yellow-300" />
              <span className="text-white/90 font-medium text-sm">8-Month Program · Aligned with Your Practicum</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Support for Your<br />Entire Practicum
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Join when your practicum starts, graduate when it ends. 8 months of community, resources, and growth.
            </p>
          </div>

          {/* Tier Cards */}
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
            {/* Core Card */}
            <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 md:p-8 border border-white/20 hover:border-white/40 hover:bg-white/15 transition-all group">
              <div className="flex items-center justify-between mb-4">
                <span className="text-violet-200 font-semibold text-sm uppercase tracking-wider">Core</span>
                <div className="flex items-baseline gap-1">
                  <span className="text-sm text-white/50 line-through">$75</span>
                  <span className="text-3xl font-bold text-white">$45</span>
                  <span className="text-white/70">/mo</span>
                </div>
              </div>

              <p className="text-white/60 text-xs mb-4">8-month commitment · $360 total</p>

              <p className="text-white/80 mb-6">Community, resources, and tools to support your entire practicum.</p>

              <ul className="space-y-3 mb-8">
                {[
                  "Private Slack community",
                  "Full resource library",
                  "Hours tracking tools",
                  "Monthly LevelUp Labs",
                  "Member discounts"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/90">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>

              <Button
                size="lg"
                className="w-full bg-white/20 hover:bg-white/30 text-white border border-white/30 py-5 font-semibold group-hover:bg-white group-hover:text-purple-700 transition-all"
                onClick={() => window.location.href = '/between-sessions/join?tier=core&student=true'}
              >
                Choose Core
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>

            {/* Community Card */}
            <div className="relative bg-white rounded-3xl p-6 md:p-8 shadow-2xl">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-gradient-to-r from-rose-500 to-pink-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg uppercase tracking-wide">
                  Most Popular
                </span>
              </div>

              <div className="flex items-center justify-between mb-4 pt-2">
                <span className="text-rose-600 font-semibold text-sm uppercase tracking-wider">Community</span>
                <div className="flex items-baseline gap-1">
                  <span className="text-sm text-gray-400 line-through">$125</span>
                  <span className="text-3xl font-bold text-gray-900">$95</span>
                  <span className="text-gray-500">/mo</span>
                </div>
              </div>

              <p className="text-gray-400 text-xs mb-4">8-month commitment · $760 total</p>

              <p className="text-gray-600 mb-6">Everything in Core plus supervision and peer support.</p>

              <ul className="space-y-3 mb-8">
                {[
                  "Everything in Core",
                  "Monthly group supervision",
                  "Monthly peer group",
                  "À la carte supervision access",
                  "Priority workshop access"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-rose-500 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>

              <Button
                size="lg"
                className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white py-5 font-semibold shadow-lg hover:shadow-xl transition-all"
                onClick={() => window.location.href = '/between-sessions/join?tier=community&student=true'}
              >
                Choose Community
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Bottom info */}
          <div className="flex flex-wrap justify-center gap-6 md:gap-10 text-white/80">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-violet-300" />
              <span>{nextCohort.name} starts {nextCohort.displayDate}</span>
            </div>
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-violet-300" />
              <span>8 months of support</span>
            </div>
            <div className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-violet-300" />
              <span>Aligned with your practicum</span>
            </div>
          </div>
        </div>
      </section>

      {/* Styles for animations */}
      <style jsx>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        .animate-marquee {
          animation: marquee 30s linear infinite;
        }

        .animate-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
}
